import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { adminApi } from '../../lib/api';

interface Stats {
  pending_vehicle_reviews: number;
  pending_reservation_reviews: number;
  pending_settlements: number;
  active_reservations: number;
  today_pickups: number;
  today_returns: number;
  open_disputes: number;
  upcoming_pickups: any[];
  upcoming_returns: any[];
}

export default function AdminDashboard() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminApi.dashboard().then((res: any) => {
      setStats(res);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" /></div>;
  }

  const today = new Date();
  const todayStr = today.toISOString().split('T')[0];
  const formatDate = (d: string) => {
    if (!d) return '-';
    const dt = new Date(d);
    const diff = Math.ceil((dt.getTime() - today.getTime()) / 86400000);
    const day = ['일','월','화','수','목','금','토'][dt.getDay()];
    const label = d === todayStr ? '오늘' : diff === 1 ? '내일' : diff <= 3 ? `${diff}일 후` : '';
    return `${dt.getMonth()+1}/${dt.getDate()} (${day})` + (label ? ` · ${label}` : '');
  };

  const isToday = (d: string) => d === todayStr;

  const cards = [
    { label: '차량 승인 대기', value: stats?.pending_vehicle_reviews || 0, color: 'bg-yellow-50 border-yellow-200', text: 'text-yellow-800', href: '/admin/vehicles' },
    { label: '예약 검토 대기', value: stats?.pending_reservation_reviews || 0, color: 'bg-blue-50 border-blue-200', text: 'text-blue-800', href: '/admin/reservations' },
    { label: '진행 중인 예약', value: stats?.active_reservations || 0, color: 'bg-green-50 border-green-200', text: 'text-green-800', href: '/admin/reservations' },
  ];

  return (
    <div>
      <h1 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6">관리자 대시보드</h1>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6 sm:mb-8">
        {cards.map(card => (
          <Link key={card.label} to={card.href} className={`${card.color} border rounded-xl p-5 h-full hover:shadow-md transition-shadow focus:outline-none focus:ring-2 focus:ring-brand-500`}>
            <p className="text-2xl font-bold mb-0.5">{card.value}</p>
            <p className={`text-xs font-medium ${card.text}`}>{card.label}</p>
          </Link>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-5">
        {/* Upcoming pickups */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <h2 className="font-semibold text-slate-900 mb-1">🚗 인도 예정</h2>
          <p className="text-xs text-gray-400 mb-3">확정된 예약의 인도 일정</p>
          {(stats?.upcoming_pickups || []).length === 0 ? (
            <p className="text-sm text-gray-400 py-8 text-center">예정된 인도가 없습니다</p>
          ) : (
            <div className="space-y-1.5">
              {(stats?.upcoming_pickups || []).map((r: any) => (
                <Link key={r.id} to={`/admin/reservations?expand=${r.id}`}
                  className={`flex items-center justify-between p-3 rounded-xl transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500 ${
                    isToday(r.pickup_date) ? 'bg-orange-50 hover:bg-orange-100 border border-orange-200' : 'bg-gray-50 hover:bg-gray-100'
                  }`}>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-900 truncate">{r.vehicle_make} {r.vehicle_model}</p>
                    <p className="text-xs text-gray-500">{r.guest_name} · {r.guest_phone}</p>
                  </div>
                  <div className="text-right shrink-0 ml-3">
                    <p className={`text-xs font-semibold ${isToday(r.pickup_date) ? 'text-orange-600' : 'text-gray-500'}`}>
                      {formatDate(r.pickup_date)}
                    </p>
                    <p className="text-xs text-gray-400">{r.rental_days}일</p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Upcoming returns */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <h2 className="font-semibold text-slate-900 mb-1">📅 반납 예정</h2>
          <p className="text-xs text-gray-400 mb-3">현재 대여 중인 차량의 반납 일정</p>
          {(stats?.upcoming_returns || []).length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-gray-400">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
                <line x1="3" y1="3" x2="21" y2="21" stroke="currentColor" strokeWidth={2} strokeLinecap="round" />
              </svg>
              <p className="text-sm">예정된 반납이 없습니다</p>
            </div>
          ) : (
            <div className="space-y-1.5">
              {(stats?.upcoming_returns || []).map((r: any) => (
                <Link key={r.id} to={`/admin/reservations?expand=${r.id}`}
                  className={`flex items-center justify-between p-3 rounded-xl transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500 ${
                    isToday(r.return_date) ? 'bg-teal-50 hover:bg-teal-100 border border-teal-200' : 'bg-gray-50 hover:bg-gray-100'
                  }`}>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-900 truncate">{r.vehicle_make} {r.vehicle_model}</p>
                    <p className="text-xs text-gray-500">{r.guest_name} · {r.guest_phone}</p>
                  </div>
                  <div className="text-right shrink-0 ml-3">
                    <p className={`text-xs font-semibold ${isToday(r.return_date) ? 'text-teal-600' : 'text-gray-500'}`}>
                      {formatDate(r.return_date)}
                    </p>
                    <p className="text-xs text-gray-400">인도: {formatDate(r.pickup_date)}</p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
